/**
 * Mock data for the Analytics deep-dive page. Distinct from the Dashboard's
 * mock data (src/features/dashboard/components/mockData.ts) — these are
 * conversation-performance metrics (automation, funnels, intents, sentiment)
 * rather than the Dashboard's high-level lead/activity overview.
 *
 * Numbers use a deterministic hash-noise generator (not Math.random) so the
 * page renders identically on every mount/re-render for a given time range —
 * no flicker, no need for seeded RNG state.
 */

export type TimeRange = "7d" | "30d" | "90d";

export const TIME_RANGE_OPTIONS: { value: TimeRange; label: string }[] = [
  { value: "7d", label: "7D" },
  { value: "30d", label: "30D" },
  { value: "90d", label: "90D" },
];

export interface MessageVolumePoint {
  date: string;
  label: string;
  messenger: number;
  instagram: number;
  whatsapp: number;
}

export interface ResponseTimePoint {
  date: string;
  label: string;
  aiSeconds: number;
  humanSeconds: number;
}

export interface FunnelStage {
  stage: string;
  value: number;
}

export type PlatformName = "Messenger" | "Instagram" | "WhatsApp" | "Website";

export interface PlatformStat {
  platform: PlatformName;
  messages: number;
  leads: number;
  convRate: number;
  avgResponseSeconds: number;
  csat: number;
}

export interface IntentItem {
  label: string;
  count: number;
}

export interface SentimentSplit {
  positive: number;
  neutral: number;
  negative: number;
  csatScore: number;
  totalRatings: number;
}

export interface AutomationRate {
  automatedPct: number;
  escalatedPct: number;
  trendPct: number;
}

export interface AnalyticsSummary {
  totalConversations: number;
  totalConversationsTrendPct: number;
  automation: AutomationRate;
  firstResponseSeconds: number;
  firstResponseTrendPct: number;
  resolutionMinutes: number;
  resolutionTrendPct: number;
  csatScore: number;
  csatTrendPct: number;
}

export interface AnalyticsData {
  range: TimeRange;
  rangeLabel: string;
  summary: AnalyticsSummary;
  messageVolume: MessageVolumePoint[];
  responseTime: ResponseTimePoint[];
  funnel: FunnelStage[];
  platforms: PlatformStat[];
  topIntents: IntentItem[];
  sentiment: SentimentSplit;
}

/** Deterministic 0..1 pseudo-random hash — same inputs always produce the same output. */
function noise(i: number, seed: number): number {
  const x = Math.sin(i * 12.9898 + seed * 78.233) * 43758.5453;
  return x - Math.floor(x);
}

const RANGE_CONFIG: Record<
  TimeRange,
  { buckets: number; bucketDays: number; seed: number; label: string }
> = {
  "7d": { buckets: 7, bucketDays: 1, seed: 7, label: "Last 7 days" },
  "30d": { buckets: 15, bucketDays: 2, seed: 30, label: "Last 30 days" },
  "90d": { buckets: 13, bucketDays: 7, seed: 90, label: "Last 90 days" },
};

function bucketDate(bucketsAgo: number, bucketDays: number): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - bucketsAgo * bucketDays);
  return d;
}

function formatLabel(d: Date): string {
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function buildMessageVolume(range: TimeRange): MessageVolumePoint[] {
  const { buckets, bucketDays, seed } = RANGE_CONFIG[range];
  const points: MessageVolumePoint[] = [];
  for (let i = buckets - 1; i >= 0; i--) {
    const d = bucketDate(i, bucketDays);
    const growth = 1 + (buckets - 1 - i) * 0.015; // gentle upward trend
    const base = 38 * bucketDays * growth;
    const messenger = Math.round(base * (0.85 + noise(i, seed) * 0.5));
    const instagram = Math.round(base * 0.72 * (0.8 + noise(i, seed + 11) * 0.55));
    const whatsapp = Math.round(base * 0.6 * (0.8 + noise(i, seed + 23) * 0.5));
    points.push({
      date: d.toISOString(),
      label: formatLabel(d),
      messenger,
      instagram,
      whatsapp,
    });
  }
  return points;
}

function buildResponseTime(range: TimeRange): ResponseTimePoint[] {
  const { buckets, bucketDays, seed } = RANGE_CONFIG[range];
  const points: ResponseTimePoint[] = [];
  for (let i = buckets - 1; i >= 0; i--) {
    const d = bucketDate(i, bucketDays);
    const improvement = (buckets - 1 - i) * 0.03; // AI gets marginally faster over time
    const aiSeconds = Math.max(2.2, 4.8 - improvement - noise(i, seed + 41) * 1.2);
    const humanSeconds = 210 + noise(i, seed + 53) * 90;
    points.push({
      date: d.toISOString(),
      label: formatLabel(d),
      aiSeconds: Math.round(aiSeconds * 10) / 10,
      humanSeconds: Math.round(humanSeconds),
    });
  }
  return points;
}

function buildFunnel(range: TimeRange): FunnelStage[] {
  const scale = range === "7d" ? 1 : range === "30d" ? 4.2 : 12.5;
  const messages = Math.round(2480 * scale);
  const engaged = Math.round(messages * 0.68);
  const leads = Math.round(engaged * 0.42);
  const converted = Math.round(leads * 0.31);
  return [
    { stage: "Messages Received", value: messages },
    { stage: "Engaged (2+ replies)", value: engaged },
    { stage: "Leads Captured", value: leads },
    { stage: "Converted (Sale)", value: converted },
  ];
}

function buildPlatforms(range: TimeRange): PlatformStat[] {
  const scale = range === "7d" ? 1 : range === "30d" ? 4.2 : 12.5;
  return [
    {
      platform: "Messenger",
      messages: Math.round(980 * scale),
      leads: Math.round(142 * scale),
      convRate: 14.5,
      avgResponseSeconds: 3.8,
      csat: 4.6,
    },
    {
      platform: "Instagram",
      messages: Math.round(705 * scale),
      leads: Math.round(88 * scale),
      convRate: 12.5,
      avgResponseSeconds: 4.4,
      csat: 4.4,
    },
    {
      platform: "WhatsApp",
      messages: Math.round(588 * scale),
      leads: Math.round(101 * scale),
      convRate: 17.2,
      avgResponseSeconds: 3.1,
      csat: 4.8,
    },
    {
      platform: "Website",
      messages: Math.round(215 * scale),
      leads: Math.round(24 * scale),
      convRate: 11.2,
      avgResponseSeconds: 5.6,
      csat: 4.2,
    },
  ];
}

function buildTopIntents(range: TimeRange): IntentItem[] {
  const scale = range === "7d" ? 1 : range === "30d" ? 4.2 : 12.5;
  return [
    { label: "Price?", count: Math.round(612 * scale) },
    { label: "Is this in stock?", count: Math.round(487 * scale) },
    { label: "Delivery / shipping cost?", count: Math.round(401 * scale) },
    { label: "Where is my order?", count: Math.round(356 * scale) },
    { label: "Do you have this in [color/size]?", count: Math.round(298 * scale) },
    { label: "Can I pay with cash on delivery?", count: Math.round(214 * scale) },
    { label: "Return / exchange policy?", count: Math.round(167 * scale) },
  ];
}

function buildSentiment(range: TimeRange): SentimentSplit {
  const scale = range === "7d" ? 1 : range === "30d" ? 4.2 : 12.5;
  return {
    positive: 74,
    neutral: 19,
    negative: 7,
    csatScore: 4.6,
    totalRatings: Math.round(340 * scale),
  };
}

function buildSummary(
  range: TimeRange,
  messageVolume: MessageVolumePoint[],
  platforms: PlatformStat[]
): AnalyticsSummary {
  const totalConversations = platforms.reduce((sum, p) => sum + p.messages, 0);
  const automatedPct = range === "7d" ? 81 : range === "30d" ? 78 : 74;
  return {
    totalConversations,
    totalConversationsTrendPct: 9.4,
    automation: {
      automatedPct,
      escalatedPct: 100 - automatedPct,
      trendPct: 3.2,
    },
    firstResponseSeconds: 3.6,
    firstResponseTrendPct: -12,
    resolutionMinutes: 8.4,
    resolutionTrendPct: -6.5,
    csatScore: 4.6,
    csatTrendPct: 2.1,
  };
}

export function getEmptyAnalyticsData(range: TimeRange): AnalyticsData {
  const { buckets, bucketDays, label } = RANGE_CONFIG[range];
  const messageVolume: MessageVolumePoint[] = [];
  const responseTime: ResponseTimePoint[] = [];

  for (let i = buckets - 1; i >= 0; i--) {
    const d = bucketDate(i, bucketDays);
    const dateLabel = formatLabel(d);
    messageVolume.push({
      date: d.toISOString(),
      label: dateLabel,
      messenger: 0,
      instagram: 0,
      whatsapp: 0,
    });
    responseTime.push({
      date: d.toISOString(),
      label: dateLabel,
      aiSeconds: 0,
      humanSeconds: 0,
    });
  }

  const platforms: PlatformStat[] = [
    { platform: "Messenger", messages: 0, leads: 0, convRate: 0, avgResponseSeconds: 0, csat: 0 },
    { platform: "Instagram", messages: 0, leads: 0, convRate: 0, avgResponseSeconds: 0, csat: 0 },
    { platform: "WhatsApp", messages: 0, leads: 0, convRate: 0, avgResponseSeconds: 0, csat: 0 },
    { platform: "Website", messages: 0, leads: 0, convRate: 0, avgResponseSeconds: 0, csat: 0 },
  ];

  const funnel: FunnelStage[] = [
    { stage: "Messages Received", value: 0 },
    { stage: "Engaged (2+ replies)", value: 0 },
    { stage: "Leads Captured", value: 0 },
    { stage: "Converted (Sale)", value: 0 },
  ];

  return {
    range,
    rangeLabel: label,
    summary: {
      totalConversations: 0,
      totalConversationsTrendPct: 0,
      automation: {
        automatedPct: 0,
        escalatedPct: 0,
        trendPct: 0,
      },
      firstResponseSeconds: 0,
      firstResponseTrendPct: 0,
      resolutionMinutes: 0,
      resolutionTrendPct: 0,
      csatScore: 0,
      csatTrendPct: 0,
    },
    messageVolume,
    responseTime,
    funnel,
    platforms,
    topIntents: [],
    sentiment: {
      positive: 0,
      neutral: 0,
      negative: 0,
      csatScore: 0,
      totalRatings: 0,
    },
  };
}

export function getAnalyticsData(range: TimeRange): AnalyticsData {
  return getEmptyAnalyticsData(range);
}

